/**
 * Graph API Service
 * Graph query operations for the RILE Connect ecosystem visualization
 */

import { useMemo } from 'react';

import { useAuthenticatedAxios } from './axiosInstance';

// Hook for graph API operations
export const useGraphApi = () => {
    const api = useAuthenticatedAxios();

    return useMemo(
        () => ({
            /**
             * Fetch full ecosystem graph (public, cached 5 min)
             * @param {Object} filters - Optional filters (sector, region, programmingTypes, hasWebsite, hasDescription)
             * @returns {Promise<Object>} Graph with nodes and edges
             */
            fetchFullGraph: async (filters = {}) => {
                const params = {};
                if (filters.sector) params.sector = filters.sector;
                if (filters.region) params.region = filters.region;
                if (filters.programmingTypes) {
                    params.programmingTypes = filters.programmingTypes;
                }
                if (filters.hasWebsite) params.hasWebsite = filters.hasWebsite;
                if (filters.hasDescription) {
                    params.hasDescription = filters.hasDescription;
                }

                const response = await api.get('/api/graph/full', {
                    params,
                    timeout: 30000,
                });
                return response.data;
            },

            /**
             * Fetch network statistics (public)
             * @returns {Promise<Object>} Network statistics
             */
            fetchStats: async () => {
                const response = await api.get('/api/graph/stats');
                return response.data;
            },

            /**
             * Fetch most connected organizations (public)
             * @param {number} limit - Number of results (default: 10, max: 50)
             * @returns {Promise<Object>} Most connected organizations
             */
            fetchMostConnected: async (limit = 10) => {
                const response = await api.get('/api/graph/most-connected', {
                    params: { limit },
                });
                return response.data;
            },

            /**
             * Fetch organization neighbors (public)
             * @param {string} id - Organization ID
             * @param {number} depth - Hop depth (1 or 2)
             * @returns {Promise<Object>} Neighbors list
             */
            fetchNeighbors: async (id, depth = 1) => {
                const response = await api.get(`/api/graph/neighbors/${id}`, {
                    params: { depth },
                });
                return response.data;
            },

            /**
             * Fetch organization subgraph for visualization (public)
             * @param {string} id - Organization ID
             * @param {number} depth - Hop depth (1 or 2)
             * @returns {Promise<Object>} Subgraph with nodes and edges
             */
            fetchSubgraph: async (id, depth = 1) => {
                const response = await api.get(`/api/graph/subgraph/${id}`, {
                    params: { depth },
                });
                return response.data;
            },

            /**
             * Find path between two organizations (public)
             * @param {string} fromId - Source organization ID
             * @param {string} toId - Target organization ID
             * @returns {Promise<Object>} Path information
             */
            findPath: async (fromId, toId) => {
                const response = await api.get('/api/graph/path', {
                    params: { from: fromId, to: toId },
                });
                return response.data;
            },

            /**
             * Fetch user's network graph (affiliations)
             * @param {string} userId - User ID
             * @returns {Promise<Object>} User-centered graph with affiliated organizations
             */
            fetchUserGraph: async (userId) => {
                const response = await api.get(`/api/graph/user/${userId}`);
                return response.data;
            },

            /**
             * Fetch cross-sector collaboration gap analysis
             * @returns {Promise<Object>} Connection counts between sector pairs
             */
            fetchCollaborationGaps: async () => {
                const response = await api.get('/api/graph/collaboration-gaps');
                return response.data;
            },

            /**
             * Fetch cross-sector partnership pairs
             * @param {string} sectorA - First sector
             * @param {string} sectorB - Second sector
             * @param {string} region - Optional region filter
             * @returns {Promise<Object>} Matching partnership graph
             */
            fetchPartnerships: async (sectorA, sectorB, region) => {
                const params = { sectorA, sectorB };
                if (region) params.region = region;
                const response = await api.get('/api/graph/partnerships', {
                    params,
                });
                return response.data;
            },

            /**
             * Fetch network metrics for an organization
             * @param {string} orgId - Organization ID
             * @returns {Promise<Object>} Organization network metrics
             */
            fetchOrgMetrics: async (orgId) => {
                const response = await api.get(
                    `/api/graph/org-metrics/${orgId}`
                );
                return response.data;
            },
        }),
        [api]
    );
};

export default useGraphApi;
